import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { useState } from 'react'

import {
  buildMonthCells,
  buildYearCells,
  type CalendarView,
  DATE_RANGE_FORMAT,
  type DateRangeValue,
  getCalendarViewTitle,
  isNextViewDisabled,
  shiftCalendarView,
} from '../lib/dateRangeCalendar'
import cn from './CalendarPanel.module.scss'
import DaysGrid from './DaysGrid/DaysGrid'
import PeriodGrid from './PeriodGrid/PeriodGrid'

const NEXT_VIEW: Record<CalendarView, CalendarView | null> = {
  days: 'months',
  months: 'years',
  years: null,
}

type CalendarPanelProps = {
  title: string
  value: Dayjs | null
  range: DateRangeValue
  maxDate: Dayjs | null
  onSelect: (date: Dayjs | null) => void
}

function CalendarPanel({
  title,
  value,
  range,
  maxDate,
  onSelect,
}: CalendarPanelProps) {
  const today = dayjs()
  const [month, setMonth] = useState(() => (value ?? today).startOf('month'))
  const [view, setView] = useState<CalendarView>('days')
  const zoomOutView = NEXT_VIEW[view]
  const periodParams = { value, today, maxDate }

  const selectDay = (day: Dayjs) => {
    onSelect(day)
    setView('days')
    if (!day.isSame(month, 'month')) setMonth(day.startOf('month'))
  }

  return (
    <section className={cn.panel}>
      <div className={cn.panelTitle}>
        <span>{title}</span>
        <strong>{value ? value.format(DATE_RANGE_FORMAT) : '—'}</strong>
      </div>

      <div className={cn.header}>
        <button
          type="button"
          className={cn.navButton}
          onClick={() => setMonth(shiftCalendarView(month, view, -1))}
          aria-label="Назад"
        >
          <LeftOutlined />
        </button>
        <button
          type="button"
          className={cn.viewTitle}
          disabled={zoomOutView === null}
          onClick={() => zoomOutView && setView(zoomOutView)}
        >
          {getCalendarViewTitle(month, view)}
        </button>
        <button
          type="button"
          className={cn.navButton}
          onClick={() => setMonth(shiftCalendarView(month, view, 1))}
          disabled={isNextViewDisabled(month, view, maxDate)}
          aria-label="Вперёд"
        >
          <RightOutlined />
        </button>
      </div>

      {view === 'days' && (
        <DaysGrid
          month={month}
          value={value}
          range={range}
          today={today}
          maxDate={maxDate}
          onSelect={selectDay}
        />
      )}

      {view === 'months' && (
        <PeriodGrid
          cells={buildMonthCells(month, periodParams)}
          onSelect={(cell) => {
            setMonth(cell.date)
            setView('days')
          }}
        />
      )}

      {view === 'years' && (
        <PeriodGrid
          cells={buildYearCells(month, periodParams)}
          onSelect={(cell) => {
            setMonth(cell.date)
            setView('months')
          }}
        />
      )}

      <div className={cn.footer}>
        <button
          type="button"
          className={cn.todayButton}
          onClick={() => selectDay(today)}
        >
          Сегодня
        </button>
        <button
          type="button"
          className={cn.clearButton}
          onClick={() => onSelect(null)}
          disabled={value === null}
        >
          Сбросить
        </button>
      </div>
    </section>
  )
}

export default CalendarPanel
